import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import BRAND from '../brand';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

// ── Lead pipeline stages ─────────────────────────────────────
const STAGES = [
  { key:'prospect',  label:'Prospect',    color:'#71717a', desc:'Found, not yet contacted' },
  { key:'contacted', label:'Contacted',   color:'#3b82f6', desc:'Reached out, awaiting reply' },
  { key:'replied',   label:'Replied',     color:'#8b5cf6', desc:'Responded — conversation started' },
  { key:'piloting',  label:'Piloting',    color:BRAND.accent, desc:'Free pilot running on their site' },
  { key:'closed',    label:'Closed Won',  color:'#10b981', desc:'Converted to paying customer' },
  { key:'lost',      label:'Lost',        color:'#ef4444', desc:'Not interested / no response' },
];

const OUTREACH_TEMPLATES = [
  {
    name: 'Cold DM — Shopify store',
    channel: 'Instagram / Facebook',
    body: `Hey [Name]! Quick question — do you handle customer support yourself or do you have someone helping? I build AI-powered chat assistants for Shopify stores that handle the repetitive questions (shipping, returns, sizing, etc.) automatically. Most store owners I work with say it saves them 5–10 hrs a week. Would you be open to a free 2-week trial on your store? No catch, just want to show you how it works 🙂`,
  },
  {
    name: 'Cold Email — SaaS founder',
    channel: 'Email',
    body: `Subject: Your customers are asking the same questions every day\n\nHi [Name],\n\nI came across [Company] and noticed you're handling support with a basic contact form.\n\nI set up AI support bots for SaaS companies that handle onboarding questions, how-to questions, and pricing questions automatically — 24/7, using your exact knowledge base.\n\nAverage deflection rate is 72%. That means 7 out of 10 support conversations never reach your inbox.\n\nCan I set up a 14-day free pilot on [Company]'s site? Takes 48 hours to go live.\n\n[Your name]`,
  },
  {
    name: 'LinkedIn DM — Founder',
    channel: 'LinkedIn',
    body: `Hi [Name], love what you're building at [Company]. Quick one — how are you handling customer support right now? Asking because I set up AI support bots for founders that handle the repetitive questions automatically. Happy to set one up free for 2 weeks if you'd want to see how it works. No dev work needed on your end.`,
  },
  {
    name: 'Follow-Up (no reply)',
    channel: 'Any',
    body: `Hey — just following up on my message. Totally understand if it's not the right time. If you ever want to try the free pilot, the offer stands. Either way, good luck with the store!`,
  },
  {
    name: 'Pilot Close (after 14 days)',
    channel: 'Email',
    body: `Subject: Your bot stats from the last 14 days\n\nHi [Name],\n\nYour bot has been live for 2 weeks — here's how it went:\n\n✓ [X] total conversations handled\n✓ [X]% resolved without human involvement\n✓ Estimated [X] hours saved\n\nIf you want to keep it running, it's $97/month. I can send a payment link now and it'll continue seamlessly.\n\nLet me know!`,
  },
];

const NAV = [
  { key:'pipeline',   icon:'📋', label:'My Pipeline'     },
  { key:'leads',      icon:'👥', label:'All Leads'       },
  { key:'add',        icon:'➕', label:'Add Lead'        },
  { key:'templates',  icon:'✉️', label:'Outreach Scripts' },
  { key:'stats',      icon:'📊', label:'My Stats'        },
  { key:'guide',      icon:'📖', label:'Playbook'        },
];

const Card = ({ children, style }) => (
  <div style={{ background:'#111113', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, padding:'1.5rem', ...style }}>{children}</div>
);

const Btn = ({ children, onClick, variant='primary', small, disabled }) => {
  const base  = { border:'none', borderRadius:8, fontFamily:'Geist,sans-serif', fontWeight:600, cursor:disabled?'not-allowed':'pointer', opacity:disabled?0.5:1, transition:'all 0.15s', whiteSpace:'nowrap' };
  const sizes = small ? { fontSize:'0.72rem', padding:'0.3rem 0.65rem' } : { fontSize:'0.85rem', padding:'0.6rem 1.2rem' };
  const vars  = {
    primary: { background:BRAND.accent, color:BRAND.accentText },
    ghost:   { background:'rgba(255,255,255,0.05)', color:'#a1a1aa', border:'1px solid rgba(255,255,255,0.1)' },
    danger:  { background:'rgba(239,68,68,0.1)', color:'#ef4444', border:'1px solid rgba(239,68,68,0.2)' },
    blue:    { background:'rgba(59,130,246,0.1)', color:'#3b82f6', border:'1px solid rgba(59,130,246,0.2)' },
    green:   { background:'rgba(16,185,129,0.1)', color:'#10b981', border:'1px solid rgba(16,185,129,0.2)' },
  };
  return <button onClick={onClick} disabled={disabled} style={{ ...base, ...sizes, ...vars[variant] }}>{children}</button>;
};

const inp = { width:'100%', padding:'0.65rem 0.9rem', background:'#18181b', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, color:'#fafafa', fontSize:'0.875rem', fontFamily:'Geist,sans-serif', outline:'none' };

export default function WorkerDashboard() {
  const { user, logout, workerAddLead, workerUpdateLead } = useAuth();
  const [tab, setTab]         = useState('pipeline');
  const [leads, setLeads]     = useState(user?.leads || []);
  const [selectedLead, setSelectedLead] = useState(null);
  const [copiedTemplate, setCopiedTemplate] = useState(null);
  const [leadSearch, setLeadSearch] = useState('');

  // Add lead form
  const [form, setForm] = useState({
    businessName:'', contactName:'', email:'', phone:'',
    website:'', businessType:'shopify', notes:'', source:'instagram',
  });
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  useEffect(() => {
    setLeads(user?.leads || []);
  }, [user]);

  // ── Derived stats ────────────────────────────────────────────
  const closed     = leads.filter(l => l.status === 'closed').length;
  const piloting   = leads.filter(l => l.status === 'piloting').length;
  const closeRate  = leads.length ? Math.round((closed / leads.length) * 100) : 0;
  const commission = closed * 297 * (user?.commissionRate || 0.15);
  const quotaPct   = user?.quota ? Math.round((user.quota.current / user.quota.monthly) * 100) : 0;

  const weeklyData = [
    { d:'Mon', reached:2 }, { d:'Tue', reached:4 }, { d:'Wed', reached:1 },
    { d:'Thu', reached:5 }, { d:'Fri', reached:3 }, { d:'Sat', reached:1 }, { d:'Sun', reached:0 },
  ];

  // ── Actions ──────────────────────────────────────────────────
  const handleAddLead = () => {
    setFormError('');
    if (!form.businessName.trim() || !form.contactName.trim()) {
      setFormError('Business name and contact name are required');
      return;
    }
    const result = workerAddLead({
      businessName: form.businessName,
      contactName:  form.contactName,
      email:        form.email,
      phone:        form.phone,
      website:      form.website,
      businessType: form.businessType,
      source:       form.source,
      notes:        form.notes,
      status:       'prospect',
    });
    if (result.error) { setFormError(result.error); return; }
    setLeads(prev => [...prev, result.lead]);
    setForm({ businessName:'', contactName:'', email:'', phone:'', website:'', businessType:'shopify', notes:'', source:'instagram' });
    setFormSuccess(`${form.businessName} added to your pipeline!`);
    setTimeout(() => { setFormSuccess(''); setTab('pipeline'); }, 2000);
  };

  const handleUpdateStatus = (leadId, newStatus) => {
    workerUpdateLead(leadId, { status: newStatus });
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: newStatus } : l));
    if (selectedLead?.id === leadId) setSelectedLead(prev => ({ ...prev, status: newStatus }));
  };

  const handleAddNote = (leadId, note) => {
    if (!note.trim()) return;
    const update = { lastNote: note, lastContactDate: new Date().toISOString() };
    workerUpdateLead(leadId, update);
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, ...update } : l));
    if (selectedLead?.id === leadId) setSelectedLead(prev => ({ ...prev, ...update }));
  };

  const copyTemplate = (body, i) => {
    navigator.clipboard?.writeText(body);
    setCopiedTemplate(i);
    setTimeout(() => setCopiedTemplate(null), 2000);
  };

  // ── Pipeline view (Kanban columns) ───────────────────────────
  const PipelinePanel = () => (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'1rem', marginBottom:'1.5rem' }}>
        {[
          { l:'Total Leads',    v:leads.length,   c:'#fafafa'  },
          { l:'Active Pilots',  v:piloting,        c:BRAND.accent },
          { l:'Closed (This Month)', v:closed,     c:'#10b981'  },
          { l:'Commission Earned',   v:`$${commission.toFixed(0)}`, c:'#10b981' },
        ].map(s => (
          <Card key={s.l}>
            <div style={{ fontSize:'0.68rem', color:'#71717a', fontWeight:600, letterSpacing:'1px', textTransform:'uppercase', marginBottom:'0.5rem' }}>{s.l}</div>
            <div style={{ fontSize:'1.75rem', fontWeight:700, color:s.c, fontFamily:'Geist Mono,monospace', lineHeight:1 }}>{s.v}</div>
          </Card>
        ))}
      </div>

      {/* Monthly quota bar */}
      {user?.quota && (
        <Card style={{ marginBottom:'1.5rem' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.75rem' }}>
            <div>
              <div style={{ fontWeight:600, fontSize:'0.9rem' }}>Monthly Quota</div>
              <div style={{ fontSize:'0.75rem', color:'#71717a', marginTop:2 }}>{user.quota.current} of {user.quota.monthly} new leads this month</div>
            </div>
            <span style={{ fontFamily:'Geist Mono,monospace', fontSize:'1.2rem', fontWeight:700, color:quotaPct >= 100 ? '#10b981' : BRAND.accent }}>{quotaPct}%</span>
          </div>
          <div style={{ height:8, background:'#1e1e24', borderRadius:4, overflow:'hidden' }}>
            <div style={{ height:'100%', width:`${Math.min(quotaPct, 100)}%`, background:quotaPct >= 100 ? '#10b981' : BRAND.accent, borderRadius:4, transition:'width 0.5s ease' }}/>
          </div>
        </Card>
      )}

      {/* Kanban columns */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'1rem' }}>
        {STAGES.filter(s => !['lost'].includes(s.key)).map(stage => {
          const stageLeads = leads.filter(l => l.status === stage.key);
          return (
            <div key={stage.key}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.75rem' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
                  <div style={{ width:8, height:8, borderRadius:'50%', background:stage.color }}/>
                  <span style={{ fontWeight:600, fontSize:'0.85rem', color:'#fafafa' }}>{stage.label}</span>
                </div>
                <span style={{ fontSize:'0.72rem', color:'#71717a', background:'rgba(255,255,255,0.05)', padding:'0.1rem 0.5rem', borderRadius:100 }}>{stageLeads.length}</span>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem', minHeight:80 }}>
                {stageLeads.length === 0 && (
                  <div style={{ padding:'1rem', background:'rgba(255,255,255,0.02)', border:'1px dashed rgba(255,255,255,0.08)', borderRadius:10, textAlign:'center', fontSize:'0.75rem', color:'#3f3f46' }}>
                    No leads here
                  </div>
                )}
                {stageLeads.map(lead => (
                  <div key={lead.id} onClick={() => setSelectedLead(lead)}
                    style={{ background:'#18181b', border:`1px solid ${stage.color}22`, borderRadius:10, padding:'0.85rem 1rem', cursor:'pointer', transition:'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor=`${stage.color}66`; e.currentTarget.style.transform='translateY(-1px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor=`${stage.color}22`; e.currentTarget.style.transform='none'; }}>
                    <div style={{ fontWeight:600, fontSize:'0.85rem', marginBottom:'0.2rem' }}>{lead.businessName}</div>
                    <div style={{ fontSize:'0.72rem', color:'#71717a' }}>{lead.contactName}</div>
                    {lead.lastNote && (
                      <div style={{ fontSize:'0.7rem', color:'#71717a', marginTop:'0.4rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', background:'rgba(255,255,255,0.03)', borderRadius:4, padding:'0.2rem 0.4rem' }}>
                        💬 {lead.lastNote}
                      </div>
                    )}
                    <div style={{ fontSize:'0.65rem', color:'#3f3f46', marginTop:'0.4rem' }}>
                      Added {new Date(lead.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Lead detail slide-over */}
      {selectedLead && (
        <div style={{ position:'fixed', top:0, right:0, width:400, height:'100vh', background:'#0d0d10', borderLeft:'1px solid rgba(255,255,255,0.08)', zIndex:100, overflow:'auto', padding:'1.5rem' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.25rem' }}>
            <div style={{ fontWeight:700, fontSize:'1rem' }}>{selectedLead.businessName}</div>
            <button onClick={() => setSelectedLead(null)} style={{ background:'rgba(255,255,255,0.06)', border:'none', borderRadius:8, width:30, height:30, color:'#71717a', cursor:'pointer', fontSize:'1rem' }}>✕</button>
          </div>

          {/* Contact info */}
          <div style={{ background:'#18181b', borderRadius:10, padding:'1rem', marginBottom:'1rem' }}>
            {[
              { l:'Contact',  v:selectedLead.contactName },
              { l:'Email',    v:selectedLead.email || '—' },
              { l:'Phone',    v:selectedLead.phone || '—' },
              { l:'Website',  v:selectedLead.website || '—' },
              { l:'Source',   v:selectedLead.source || '—' },
              { l:'Type',     v:selectedLead.businessType || '—' },
            ].map(r => (
              <div key={r.l} style={{ display:'flex', justifyContent:'space-between', padding:'0.35rem 0', borderBottom:'1px solid rgba(255,255,255,0.04)', fontSize:'0.82rem' }}>
                <span style={{ color:'#71717a' }}>{r.l}</span>
                <span style={{ color:'#fafafa', fontWeight:500 }}>{r.v}</span>
              </div>
            ))}
          </div>

          {/* Move stage */}
          <div style={{ marginBottom:'1rem' }}>
            <div style={{ fontSize:'0.75rem', fontWeight:600, color:'#a1a1aa', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'0.5rem' }}>Move to stage</div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'0.4rem' }}>
              {STAGES.map(s => (
                <button key={s.key} onClick={() => handleUpdateStatus(selectedLead.id, s.key)}
                  style={{ fontSize:'0.72rem', fontWeight:600, padding:'0.3rem 0.65rem', borderRadius:6, cursor:'pointer', fontFamily:'Geist,sans-serif', border:`1px solid ${selectedLead.status===s.key ? s.color : 'rgba(255,255,255,0.1)'}`, background:selectedLead.status===s.key ? `${s.color}22` : 'transparent', color:selectedLead.status===s.key ? s.color : '#71717a', transition:'all 0.15s' }}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Add note */}
          <div style={{ marginBottom:'1rem' }}>
            <div style={{ fontSize:'0.75rem', fontWeight:600, color:'#a1a1aa', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'0.5rem' }}>Add a note</div>
            <textarea id={`note_${selectedLead.id}`} placeholder="What happened in this interaction?" style={{ ...inp, minHeight:70, resize:'vertical', lineHeight:1.5 }} onFocus={e=>e.target.style.borderColor=BRAND.accent} onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.1)'}/>
            <Btn small style={{ marginTop:'0.5rem' }} onClick={() => { const el = document.getElementById(`note_${selectedLead.id}`); handleAddNote(selectedLead.id, el.value); el.value=''; }}>Save note</Btn>
          </div>

          {selectedLead.lastNote && (
            <div style={{ background:'rgba(245,158,11,0.05)', border:'1px solid rgba(245,158,11,0.15)', borderRadius:8, padding:'0.75rem', marginBottom:'1rem' }}>
              <div style={{ fontSize:'0.68rem', color:BRAND.accent, marginBottom:'0.3rem', fontWeight:600 }}>LAST NOTE</div>
              <div style={{ fontSize:'0.8rem', color:'#a1a1aa' }}>{selectedLead.lastNote}</div>
              {selectedLead.lastContactDate && <div style={{ fontSize:'0.68rem', color:'#71717a', marginTop:'0.3rem' }}>{new Date(selectedLead.lastContactDate).toLocaleString()}</div>}
            </div>
          )}

          {selectedLead.notes && (
            <div style={{ background:'#18181b', borderRadius:8, padding:'0.75rem' }}>
              <div style={{ fontSize:'0.68rem', color:'#71717a', marginBottom:'0.3rem', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px' }}>Initial Notes</div>
              <div style={{ fontSize:'0.8rem', color:'#a1a1aa' }}>{selectedLead.notes}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const AllLeadsPanel = () => {
    const filtered = leads.filter(l =>
      l.businessName?.toLowerCase().includes(leadSearch.toLowerCase()) ||
      l.contactName?.toLowerCase().includes(leadSearch.toLowerCase())
    );
    return (
      <div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.25rem' }}>
          <div>
            <h2 style={{ fontSize:'1.1rem', fontWeight:600 }}>All Leads</h2>
            <p style={{ fontSize:'0.8rem', color:'#71717a', marginTop:2 }}>{leads.length} total in your pipeline</p>
          </div>
          <input value={leadSearch} onChange={e => setLeadSearch(e.target.value)} placeholder="Search leads..." style={{ ...inp, width:220 }} onFocus={e=>e.target.style.borderColor=BRAND.accent} onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.1)'}/>
        </div>
        <Card>
          {filtered.length === 0 ? (
            <div style={{ textAlign:'center', color:'#71717a', padding:'3rem', fontSize:'0.85rem' }}>
              {leadSearch ? 'No leads match your search' : 'No leads yet — click "Add Lead" to add your first prospect'}
            </div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ fontSize:'0.68rem', color:'#71717a', textTransform:'uppercase', letterSpacing:'0.5px' }}>
                  {['Business','Contact','Source','Type','Stage','Added','Actions'].map(h => (
                    <th key={h} style={{ textAlign:'left', padding:'0.6rem 0.75rem', borderBottom:'1px solid rgba(255,255,255,0.06)', fontWeight:600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(lead => {
                  const stage = STAGES.find(s => s.key === lead.status) || STAGES[0];
                  return (
                    <tr key={lead.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)' }}
                      onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.02)'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <td style={{ padding:'0.85rem 0.75rem', fontWeight:600, fontSize:'0.875rem' }}>{lead.businessName}</td>
                      <td style={{ padding:'0.85rem 0.75rem', fontSize:'0.82rem', color:'#a1a1aa' }}>{lead.contactName}</td>
                      <td style={{ padding:'0.85rem 0.75rem', fontSize:'0.78rem', color:'#71717a', textTransform:'capitalize' }}>{lead.source}</td>
                      <td style={{ padding:'0.85rem 0.75rem', fontSize:'0.78rem', color:'#71717a', textTransform:'capitalize' }}>{lead.businessType}</td>
                      <td style={{ padding:'0.85rem 0.75rem' }}>
                        <span style={{ fontSize:'0.72rem', fontWeight:700, color:stage.color, background:`${stage.color}18`, padding:'0.2rem 0.55rem', borderRadius:4 }}>{stage.label}</span>
                      </td>
                      <td style={{ padding:'0.85rem 0.75rem', fontSize:'0.75rem', color:'#71717a' }}>{new Date(lead.createdAt).toLocaleDateString()}</td>
                      <td style={{ padding:'0.85rem 0.75rem' }}>
                        <div style={{ display:'flex', gap:'0.4rem' }}>
                          <Btn small variant="ghost" onClick={() => { setSelectedLead(lead); setTab('pipeline'); }}>View</Btn>
                          {lead.status !== 'closed' && <Btn small variant="green" onClick={() => handleUpdateStatus(lead.id, 'closed')}>Close</Btn>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    );
  };

  const AddLeadPanel = () => (
    <div>
      <h2 style={{ fontSize:'1.1rem', fontWeight:600, marginBottom:'0.35rem' }}>Add New Lead</h2>
      <p style={{ fontSize:'0.8rem', color:'#71717a', marginBottom:'1.5rem' }}>Log a new prospect to your pipeline</p>

      {formSuccess && <div style={{ background:'rgba(16,185,129,0.1)', border:'1px solid rgba(16,185,129,0.2)', borderRadius:8, padding:'0.85rem 1rem', marginBottom:'1rem', fontSize:'0.85rem', color:'#10b981' }}>✓ {formSuccess}</div>}
      {formError  && <div style={{ background:'rgba(239,68,68,0.1)',  border:'1px solid rgba(239,68,68,0.25)',  borderRadius:8, padding:'0.85rem 1rem', marginBottom:'1rem', fontSize:'0.85rem', color:'#fca5a5' }}>⚠️ {formError}</div>}

      <Card>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1.25rem' }}>
          {[
            { label:'Business Name *',  key:'businessName', type:'text',  placeholder:'Acme Shopify Store', required:true },
            { label:'Contact Name *',   key:'contactName',  type:'text',  placeholder:'Jane Smith',          required:true },
            { label:'Email',            key:'email',        type:'email', placeholder:'jane@acme.com' },
            { label:'Phone',            key:'phone',        type:'tel',   placeholder:'+1 (555) 000-0000' },
            { label:'Website',          key:'website',      type:'url',   placeholder:'https://acme.com' },
          ].map(f => (
            <div key={f.key}>
              <label style={{ fontSize:'0.78rem', color:'#a1a1aa', display:'block', marginBottom:'0.4rem' }}>{f.label}</label>
              <input type={f.type} value={form[f.key]} onChange={e => setForm(p=>({...p,[f.key]:e.target.value}))}
                placeholder={f.placeholder} style={inp}
                onFocus={e=>e.target.style.borderColor=BRAND.accent} onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.1)'}/>
            </div>
          ))}

          <div>
            <label style={{ fontSize:'0.78rem', color:'#a1a1aa', display:'block', marginBottom:'0.4rem' }}>Business Type</label>
            <select value={form.businessType} onChange={e => setForm(p=>({...p,businessType:e.target.value}))} style={{ ...inp, cursor:'pointer' }}>
              <option value="shopify">Shopify Store</option>
              <option value="saas">SaaS / Software</option>
              <option value="ecommerce">E-commerce (other)</option>
              <option value="local">Local Service Business</option>
              <option value="agency">Agency</option>
              <option value="course">Online Course / Coach</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label style={{ fontSize:'0.78rem', color:'#a1a1aa', display:'block', marginBottom:'0.4rem' }}>Where did you find them?</label>
            <select value={form.source} onChange={e => setForm(p=>({...p,source:e.target.value}))} style={{ ...inp, cursor:'pointer' }}>
              <option value="instagram">Instagram</option>
              <option value="facebook">Facebook</option>
              <option value="linkedin">LinkedIn</option>
              <option value="twitter">Twitter / X</option>
              <option value="email">Cold Email</option>
              <option value="reddit">Reddit</option>
              <option value="referral">Referral</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        <div style={{ marginTop:'1.25rem' }}>
          <label style={{ fontSize:'0.78rem', color:'#a1a1aa', display:'block', marginBottom:'0.4rem' }}>Initial Notes</label>
          <textarea value={form.notes} onChange={e => setForm(p=>({...p,notes:e.target.value}))}
            placeholder="What do you know about this prospect? Why are they a good fit?" rows={3}
            style={{ ...inp, resize:'vertical', lineHeight:1.6 }}
            onFocus={e=>e.target.style.borderColor=BRAND.accent} onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.1)'}/>
        </div>

        <div style={{ display:'flex', gap:'0.75rem', marginTop:'1.25rem' }}>
          <Btn onClick={handleAddLead}>Add to Pipeline →</Btn>
          <Btn variant="ghost" onClick={() => setForm({ businessName:'', contactName:'', email:'', phone:'', website:'', businessType:'shopify', notes:'', source:'instagram' })}>Clear form</Btn>
        </div>
      </Card>
    </div>
  );

  const TemplatesPanel = () => (
    <div>
      <h2 style={{ fontSize:'1.1rem', fontWeight:600, marginBottom:'0.35rem' }}>Outreach Scripts</h2>
      <p style={{ fontSize:'0.8rem', color:'#71717a', marginBottom:'1.5rem' }}>Copy-paste templates for every stage of the funnel. Replace [Name], [Company] etc. before sending.</p>
      <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
        {OUTREACH_TEMPLATES.map((t, i) => (
          <Card key={i}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.85rem' }}>
              <div>
                <div style={{ fontWeight:600, fontSize:'0.9rem' }}>{t.name}</div>
                <div style={{ fontSize:'0.72rem', color:'#71717a', marginTop:2 }}>Best for: {t.channel}</div>
              </div>
              <Btn small variant={copiedTemplate === i ? 'green' : 'ghost'} onClick={() => copyTemplate(t.body, i)}>
                {copiedTemplate === i ? '✓ Copied!' : 'Copy'}
              </Btn>
            </div>
            <pre style={{ background:'#0a0a0c', border:'1px solid rgba(255,255,255,0.06)', borderRadius:10, padding:'1rem', fontSize:'0.78rem', color:'#a1a1aa', lineHeight:1.65, whiteSpace:'pre-wrap', wordBreak:'break-word', margin:0, fontFamily:'Geist,sans-serif' }}>
              {t.body}
            </pre>
          </Card>
        ))}
      </div>
    </div>
  );

  const StatsPanel = () => (
    <div>
      <h2 style={{ fontSize:'1.1rem', fontWeight:600, marginBottom:'0.35rem' }}>My Stats</h2>
      <p style={{ fontSize:'0.8rem', color:'#71717a', marginBottom:'1.5rem' }}>Your personal performance this month</p>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'1rem', marginBottom:'2rem' }}>
        {[
          { l:'Total Leads',      v:leads.length,        c:'#fafafa' },
          { l:'Close Rate',       v:`${closeRate}%`,     c:'#3b82f6' },
          { l:'Deals Closed',     v:closed,              c:'#10b981' },
          { l:'Commission Earned', v:`$${commission.toFixed(0)}`, c:'#10b981' },
        ].map(s => (
          <Card key={s.l}>
            <div style={{ fontSize:'0.68rem', color:'#71717a', fontWeight:600, letterSpacing:'1px', textTransform:'uppercase', marginBottom:'0.5rem' }}>{s.l}</div>
            <div style={{ fontSize:'1.75rem', fontWeight:700, color:s.c, fontFamily:'Geist Mono,monospace', lineHeight:1 }}>{s.v}</div>
          </Card>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:'1.5rem', marginBottom:'1.5rem' }}>
        <Card>
          <div style={{ fontWeight:600, fontSize:'0.9rem', marginBottom:'0.25rem' }}>Prospects Reached This Week</div>
          <div style={{ fontSize:'0.72rem', color:'#71717a', marginBottom:'1.25rem' }}>New contacts made per day</div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={weeklyData} margin={{top:0,right:0,bottom:0,left:-30}}>
              <XAxis dataKey="d" tick={{fontSize:11,fill:'#71717a'}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:11,fill:'#71717a'}} axisLine={false} tickLine={false}/>
              <Tooltip contentStyle={{background:'#18181b',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,fontSize:12}}/>
              <Bar dataKey="reached" fill={BRAND.accent} radius={[4,4,0,0]} name="Reached"/>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card>
          <div style={{ fontWeight:600, fontSize:'0.9rem', marginBottom:'1rem' }}>Pipeline Breakdown</div>
          {STAGES.map(stage => {
            const count = leads.filter(l => l.status === stage.key).length;
            const pct   = leads.length ? Math.round((count / leads.length) * 100) : 0;
            return (
              <div key={stage.key} style={{ marginBottom:'0.75rem' }}>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.78rem', marginBottom:'0.25rem' }}>
                  <span style={{ color:'#a1a1aa' }}>{stage.label}</span>
                  <span style={{ fontWeight:600 }}>{count}</span>
                </div>
                <div style={{ height:4, background:'#1e1e24', borderRadius:2 }}>
                  <div style={{ height:'100%', width:`${pct}%`, background:stage.color, borderRadius:2 }}/>
                </div>
              </div>
            );
          })}
        </Card>
      </div>

      {/* Commission calculator */}
      <Card>
        <div style={{ fontWeight:600, fontSize:'0.9rem', marginBottom:'0.5rem' }}>Commission Calculator</div>
        <div style={{ fontSize:'0.8rem', color:'#71717a', marginBottom:'1rem' }}>Based on your current rate of {Math.round((user?.commissionRate || 0.15) * 100)}%</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'1rem' }}>
          {[
            { plan:'Starter ($97/mo)', commission:Math.round(97*(user?.commissionRate||0.15)) },
            { plan:'Growth ($297/mo)',  commission:Math.round(297*(user?.commissionRate||0.15)) },
            { plan:'Agency ($597/mo)',  commission:Math.round(597*(user?.commissionRate||0.15)) },
          ].map(p => (
            <div key={p.plan} style={{ background:'#18181b', borderRadius:10, padding:'1rem', textAlign:'center' }}>
              <div style={{ fontSize:'0.75rem', color:'#71717a', marginBottom:'0.4rem' }}>{p.plan}</div>
              <div style={{ fontSize:'1.4rem', fontWeight:700, color:'#10b981', fontFamily:'Geist Mono,monospace' }}>${p.commission}/mo</div>
              <div style={{ fontSize:'0.68rem', color:'#71717a', marginTop:2 }}>per customer</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );

  const GuidePanel = () => (
    <div>
      <h2 style={{ fontSize:'1.1rem', fontWeight:600, marginBottom:'0.35rem' }}>Sales Playbook</h2>
      <p style={{ fontSize:'0.8rem', color:'#71717a', marginBottom:'1.5rem' }}>Your step-by-step guide to closing deals</p>
      <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
        {[
          { step:'01', title:'Find your prospects', time:'20 min/day', color:'#3b82f6', content:[
            'Google: site:myshopify.com + "your niche" (e.g. site:myshopify.com "skincare")',
            'Instagram: Search hashtags like #shopowner, #ecommercebusiness, #smallbusiness',
            'LinkedIn: Search "founder" OR "CEO" + SaaS, filter to 1–50 employees',
            'Reddit: Monitor r/shopify, r/entrepreneur, r/SaaS for people asking about support tools',
            'Tip: Look for stores with NO live chat widget — that is your signal',
          ]},
          { step:'02', title:'Send the first message', time:'10 min/day', color:BRAND.accent, content:[
            'Use the "Cold DM — Shopify store" template from the Outreach Scripts tab',
            'Always personalize: mention their store name, a product you noticed, their recent post',
            'Keep it short — under 5 sentences',
            'Offer value first: "free 2-week trial" not "buy my product"',
            'Goal: Get a reply, not a sale',
          ]},
          { step:'03', title:'Follow up (most people miss this)', time:'5 min/day', color:'#8b5cf6', content:[
            'If no reply after 3–4 days, send the follow-up template',
            'Only follow up once — don\'t spam',
            'If they reply "not interested" — thank them and move on. Never push.',
            'If they\'re curious — offer to show them a 90-second demo video',
          ]},
          { step:'04', title:'Run the pilot', time:'30 min setup', color:'#10b981', content:[
            'Tell them: "I\'ll set everything up — you just paste one line on your site"',
            'Get their FAQs, pricing, and brand color from a simple form',
            'Set up the bot in your dashboard and send them the embed snippet',
            'Check in at Day 3 and Day 10 to show them conversation stats',
            'Move this lead to "Piloting" in your pipeline immediately',
          ]},
          { step:'05', title:'Close the deal', time:'2 min', color:'#10b981', content:[
            'After 14 days, send the "Pilot Close" email with their exact stats',
            'Always include: conversations handled, resolution rate, time saved',
            'The magic question: "It saved you X hours. Want to keep it running for $97/month?"',
            'Send a Stripe payment link — don\'t invoice, don\'t invoice, don\'t invoice',
            'Move lead to "Closed Won" and log the commission in your stats',
          ]},
        ].map(section => (
          <Card key={section.step} style={{ borderLeft:`3px solid ${section.color}` }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'0.85rem' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
                <div style={{ width:32, height:32, borderRadius:'50%', background:`${section.color}20`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.75rem', fontWeight:700, color:section.color, flexShrink:0 }}>
                  {section.step}
                </div>
                <div style={{ fontWeight:600, fontSize:'0.95rem' }}>{section.title}</div>
              </div>
              <span style={{ fontSize:'0.72rem', color:'#71717a', background:'rgba(255,255,255,0.05)', padding:'0.2rem 0.6rem', borderRadius:100, whiteSpace:'nowrap' }}>⏱ {section.time}</span>
            </div>
            <ul style={{ listStyle:'none', display:'flex', flexDirection:'column', gap:'0.5rem' }}>
              {section.content.map((item, i) => (
                <li key={i} style={{ fontSize:'0.83rem', color:'#a1a1aa', display:'flex', gap:'0.5rem', alignItems:'flex-start', lineHeight:1.55 }}>
                  <span style={{ color:section.color, flexShrink:0, marginTop:1 }}>→</span>
                  {item}
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderPanel = () => {
    switch(tab) {
      case 'pipeline':  return <PipelinePanel/>;
      case 'leads':     return <AllLeadsPanel/>;
      case 'add':       return <AddLeadPanel/>;
      case 'templates': return <TemplatesPanel/>;
      case 'stats':     return <StatsPanel/>;
      case 'guide':     return <GuidePanel/>;
      default:          return <PipelinePanel/>;
    }
  };

  return (
    <div style={{ minHeight:'100vh', background:'#09090b', fontFamily:'Geist,sans-serif', display:'flex' }}>

      {/* SIDEBAR */}
      <aside style={{ width:220, background:'#080a10', borderRight:'1px solid rgba(59,130,246,0.1)', display:'flex', flexDirection:'column', flexShrink:0, position:'sticky', top:0, height:'100vh' }}>
        <div style={{ padding:'1.5rem 1.25rem', borderBottom:'1px solid rgba(59,130,246,0.1)' }}>
          <div style={{ fontFamily:'Instrument Serif,serif', fontSize:'1.2rem', color:'#fafafa' }}>
            {BRAND.name}<span style={{ color:'#3b82f6' }}>.</span>
          </div>
          <div style={{ fontSize:'0.65rem', fontWeight:700, letterSpacing:'2px', textTransform:'uppercase', color:'#3b82f6', marginTop:'0.25rem' }}>Sales Portal</div>
        </div>

        <nav style={{ flex:1, padding:'0.75rem 0', overflowY:'auto' }}>
          {NAV.map(item => {
            const active = tab === item.key;
            return (
              <button key={item.key} onClick={() => setTab(item.key)}
                style={{ display:'flex', alignItems:'center', gap:'0.65rem', width:'calc(100% - 1.5rem)', margin:'0.1rem 0.75rem', padding:'0.6rem 1rem', borderRadius:8, cursor:'pointer', border:'none', textAlign:'left', background:active?'rgba(59,130,246,0.12)':'transparent', color:active?'#3b82f6':'#71717a', fontSize:'0.85rem', fontWeight:active?600:400, fontFamily:'Geist,sans-serif', transition:'all 0.15s' }}
                onMouseEnter={e => { if(!active){e.currentTarget.style.background='rgba(255,255,255,0.04)';e.currentTarget.style.color='#fafafa';} }}
                onMouseLeave={e => { if(!active){e.currentTarget.style.background='transparent';e.currentTarget.style.color='#71717a';} }}>
                <span style={{ fontSize:'0.9rem', flexShrink:0 }}>{item.icon}</span>
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* Quota mini-bar */}
        {user?.quota && (
          <div style={{ padding:'0 1.25rem', marginBottom:'0.75rem' }}>
            <div style={{ fontSize:'0.68rem', color:'#71717a', marginBottom:'0.35rem' }}>Monthly quota: {user.quota.current}/{user.quota.monthly}</div>
            <div style={{ height:4, background:'#1e1e24', borderRadius:2, overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${Math.min(quotaPct,100)}%`, background:quotaPct>=100?'#10b981':BRAND.accent, borderRadius:2 }}/>
            </div>
          </div>
        )}

        <div style={{ padding:'1rem 1.25rem', borderTop:'1px solid rgba(59,130,246,0.1)' }}>
          <div style={{ fontSize:'0.8rem', fontWeight:500, color:'#fafafa', marginBottom:'0.25rem' }}>{user?.name}</div>
          <div style={{ fontSize:'0.68rem', color:'#71717a', marginBottom:'0.65rem' }}>Sales Rep · {Math.round((user?.commissionRate||0.15)*100)}% commission</div>
          <Btn small variant="danger" onClick={logout}>Sign out</Btn>
        </div>
      </aside>

      {/* MAIN */}
      <main style={{ flex:1, overflow:'auto' }}>
        <div style={{ padding:'1.25rem 2rem', borderBottom:'1px solid rgba(255,255,255,0.06)', display:'flex', alignItems:'center', justifyContent:'space-between', background:'#09090b', position:'sticky', top:0, zIndex:10 }}>
          <div>
            <h1 style={{ fontSize:'1.1rem', fontWeight:600 }}>{NAV.find(n=>n.key===tab)?.label}</h1>
            <p style={{ fontSize:'0.72rem', color:'#71717a', marginTop:1 }}>Sales Portal · {leads.length} leads · {closed} closed</p>
          </div>
          <div style={{ display:'flex', gap:'0.75rem' }}>
            <Btn onClick={() => setTab('add')}>+ Add Lead</Btn>
            <div style={{ fontSize:'0.75rem', color:'#3b82f6', background:'rgba(59,130,246,0.1)', border:'1px solid rgba(59,130,246,0.2)', padding:'0.3rem 0.75rem', borderRadius:100 }}>
              💼 Worker
            </div>
          </div>
        </div>
        <div style={{ padding:'2rem' }}>{renderPanel()}</div>
      </main>
    </div>
  );
}